import { Form } from 'react-bootstrap';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  COUNTRIES,
  DEFAULT_COUNTRY_ISO,
  buildE164Phone,
  findCountryByIso,
  getCountryFlagSrc,
  parsePhoneWithCountry,
} from '../constants/countries';

function CountryFlag({ iso, className }) {
  return (
    <img
      className={className}
      src={getCountryFlagSrc(iso, 40)}
      alt=""
      width={18}
      height={13}
      loading="lazy"
      decoding="async"
    />
  );
}

/** Same as mosida-web PhoneInputWithCountry (flag + dial dropdown + Send OTP). */
function PhoneInputWithCountry({
  label = 'Phone number',
  name = 'phoneNumber',
  value = '',
  placeholder = 'Phone number',
  text,
  onChange,
  onSend,
  readOnly,
  sendDisabled = false,
  defaultCountryIso = DEFAULT_COUNTRY_ISO,
}) {
  const parsed = useMemo(
    () => parsePhoneWithCountry(value, defaultCountryIso),
    [value, defaultCountryIso]
  );

  const [countryIso, setCountryIso] = useState(parsed.country.iso);
  const [nationalNumber, setNationalNumber] = useState(parsed.nationalNumber);
  const [open, setOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);

  const country = findCountryByIso(countryIso);

  const filteredCountries = useMemo(() => {
    const query = countrySearch.trim().toLowerCase();
    if (!query) return COUNTRIES;

    const queryDigits = query.replace(/\D/g, '');
    return COUNTRIES.filter((item) => {
      const nameMatch = item.name.toLowerCase().includes(query);
      const isoMatch = item.iso.toLowerCase().includes(query);
      const dialMatch =
        item.dialCode.includes(queryDigits) || `+${item.dialCode}`.includes(query);
      return nameMatch || isoMatch || (queryDigits ? dialMatch : false);
    });
  }, [countrySearch]);

  useEffect(() => {
    const next = parsePhoneWithCountry(value, countryIso || defaultCountryIso);
    setCountryIso(next.country.iso);
    setNationalNumber(next.nationalNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultCountryIso]);

  useEffect(() => {
    if (!open) {
      setCountrySearch('');
      return undefined;
    }

    const focusTimer = window.setTimeout(() => {
      searchRef.current?.focus();
    }, 0);

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current || countrySearch.trim()) return;
    const active = listRef.current.querySelector(
      '.phoneInputWithCountry__option.is-active'
    );
    active?.scrollIntoView({ block: 'nearest' });
  }, [open, countryIso, countrySearch]);

  const emitChange = (nextCountry, nextNational) => {
    if (!onChange) return;
    const full = buildE164Phone(nextCountry, nextNational);
    onChange({
      target: { name, value: full },
    });
  };

  const handleNationalChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    setNationalNumber(digits);
    emitChange(country, digits);
  };

  const handleSelectCountry = (next) => {
    setCountryIso(next.iso);
    setCountrySearch('');
    setOpen(false);
    emitChange(next, nationalNumber);
  };

  const hasValue = Boolean(nationalNumber.trim()) || Boolean(value.trim());

  return (
    <div
      ref={rootRef}
      className={`floatingInput phoneInputWithCountry${hasValue ? ' has-value' : ''}${
        readOnly ? ' is-readonly' : ''
      }${open ? ' is-open' : ''}${text ? ' has-otp' : ''}`}
    >
      <div className="phoneInputWithCountry__control">
        <button
          type="button"
          className="phoneInputWithCountry__countryBtn"
          onClick={() => {
            if (readOnly) return;
            setOpen((prev) => !prev);
          }}
          disabled={readOnly}
          aria-label={`Select country, ${country.name}`}
          aria-expanded={open}
        >
          <CountryFlag iso={country.iso} className="phoneInputWithCountry__flag" />
          <span className="phoneInputWithCountry__dial">+{country.dialCode}</span>
          <span className="phoneInputWithCountry__caret" aria-hidden />
        </button>

        <Form.Control
          id={name}
          name={name}
          value={nationalNumber}
          onChange={handleNationalChange}
          readOnly={readOnly}
          className="form-control phoneInputWithCountry__input"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder={placeholder}
        />

        {text ? (
          <button
            type="button"
            className={`phoneInputWithCountry__otpBtn${
              text === 'Send OTP' ? '' : ' is-verify'
            }${sendDisabled ? ' is-disabled' : ''}`}
            onClick={sendDisabled ? undefined : onSend}
            disabled={sendDisabled}
          >
            {text}
          </button>
        ) : null}
      </div>

      <label htmlFor={name}>{label}</label>

      {open && !readOnly ? (
        <div className="phoneInputWithCountry__dropdown" role="listbox">
          <div className="phoneInputWithCountry__search">
            <input
              ref={searchRef}
              type="text"
              className="phoneInputWithCountry__searchInput"
              placeholder="Search"
              value={countrySearch}
              onChange={(e) => setCountrySearch(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              aria-label="Search dial code"
              autoComplete="off"
            />
          </div>
          <ul ref={listRef} className="phoneInputWithCountry__list">
            {filteredCountries.length === 0 ? (
              <li className="phoneInputWithCountry__empty">No matches</li>
            ) : (
              filteredCountries.map((item) => (
                <li key={`${item.iso}-${item.dialCode}`}>
                  <button
                    type="button"
                    className={`phoneInputWithCountry__option${
                      item.iso === country.iso ? ' is-active' : ''
                    }`}
                    onClick={() => handleSelectCountry(item)}
                    role="option"
                    aria-selected={item.iso === country.iso}
                  >
                    <CountryFlag
                      iso={item.iso}
                      className="phoneInputWithCountry__optionFlag"
                    />
                    <span className="phoneInputWithCountry__optionDial">
                      +{item.dialCode}
                    </span>
                    <span className="phoneInputWithCountry__optionName">{item.name}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default PhoneInputWithCountry;
